import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";

import { translate } from "@/modules/shared/IntlProviderWrapper";
import {
    classNames,
    dateFromDayShift,
    fromShiftTag,
    niceDate,
    niceNumber,
    SHIFT_CONSTS,
    shiftNumber,
    shiftTag,
} from "@/lib/util";
import { getPeopleSchedulingStrategyOption, getPeopleSchedulingStrategyOptions } from "@/lib/shifts";
import { getLineGroupForUser } from "@/lib/businessLogic";
import { getBackend } from "@/lib/backend/backend2";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import { loadPeopleSchedulingParams, storePeopleSchedulingParams } from "@/lib/shifts";
import { Toolbar, ToolbarGroup, ToolbarItem } from "@/modules/shared/Toolbar";
import { PeopleSchedulingSettings } from "@/modules/pg/shared/PeopleSchedulingSettings";
import { FIELD_PEOPLE_SCHEDULING_PARAMS } from "@/lib/localSettings";
import { PEOPLE_SCHEDULING_STRATEGY } from "@/lib/ManufacturingConsts.generated";

import type {
    IAssignPeopleToShiftsByLinesReq,
    ICompetenceData,
    ILineData,
    IPeopleScheduleLine,
    IPeopleSchedulingParams,
    IShiftLineRec,
    IShiftPersonLineRecPerson,
} from "@/lib/backend/manufacturing2.generated.types";
import type { PeopleSchedulingStrategyOption } from "@/lib/shifts";

import "./PreplanningShiftCompetencesModal.scss";

type FactorSum = {
    needed: number;
    available: number;
};

type FactorSumsByCompetence = Map<number, FactorSum>;

const DEFAULT_PEOPLE_SCHEDULING_PARAMS: IPeopleSchedulingParams = {
    allow_one_competence_per_shift: false,
    allow_one_person_per_single_person_line: false,
    allow_overwork: true,
    consider_dwell_time: false,
    consider_partial_retirement: false,
    ignore_pinned_lines: true,
    prefer_fully_scheduled_people: false,
    strategy: PEOPLE_SCHEDULING_STRATEGY.more_experienced_first,
};

/**
 * Parses a time period tag and returns a formatted string.
 * @param {string} time_period_tag - The time period tag to parse. Either a date
 * string in format "YYYY-MM-DD" or a shift tag in format "YYYYWWSS" where
 * YYYY is the year, WW is the week number, and SS is the shift number of a week.
 * @returns {string} - The formatted time period string.
 */
const parseTimePeriodTag = (time_period_tag: string): string => {
    let date = new Date(time_period_tag);
    if (!Number.isNaN(date.getTime())) {
        // If the time_period_tag is a valid date string, return parsed date
        return `${translate("common.date", "Date")}: ${niceDate(date)}`;
    }

    date = fromShiftTag(time_period_tag);
    if (!Number.isNaN(date.getTime())) {
        // If the time_period_tag is a valid shift tag, return parsed date and shift
        const shift_number = shiftNumber(date);
        const shift_labels = [
            translate("common.morning_shift", "Early"),
            translate("common.afternoon_shift", "Late"),
            translate("common.night_shift", "Night"),
        ];
        return `${translate("common.shift", "Shift")}: ${shift_labels[shift_number.shift_day]}, ${niceDate(date)}`;
    }

    // If the time_period_tag is neither a valid date nor a valid shift tag, return it as is
    return time_period_tag;
};

/**
 * Converts a time period tag to an array of shift tags.
 * @param {string} time_period_tag - The time period tag to convert.
 * It can be either a date string in format "YYYY-MM-DD" or a shift tag in format "YYYYWWSS".
 * @returns {string[]} - An array of shift tags corresponding to the time period tag.
 * If the time_period_tag is a valid shift tag, it returns an array with that shift tag.
 * If it is a valid date string, it returns an array of shift tags for that date.
 * If it is neither, it returns an empty array.
 */
const timePeriodTagToShiftTags = (time_period_tag: string): string[] => {
    let date = fromShiftTag(time_period_tag);
    if (!Number.isNaN(date.getTime())) {
        // If the time_period_tag is a valid shift tag, return the shift tag
        return [time_period_tag];
    }

    date = new Date(time_period_tag);
    if (!Number.isNaN(date.getTime())) {
        // If the time_period_tag is a valid date string, return all shift tags for that date
        const shift_tags: string[] = [];
        for (let i = 0; i < SHIFT_CONSTS.SHIFT_START_HOURS.length; i++) {
            const shift_date = dateFromDayShift(date, i);
            const shift_number = shiftNumber(shift_date);
            shift_tags.push(shift_number.shift_tag);
        }

        return shift_tags;
    }

    return [];
};

/**
 * Prepares the request object for assigning people to shifts by lines.
 * @param {number} line_group_id - The ID of the line group.
 * @param {ILineData[]} lines - The list of lines.
 * @param {ILineData[]} child_lines - The list of child lines.
 * @param {IShiftLineRec[]} shifts - The list of shifts.
 * @param {string} time_period_tag - The time period tag to filter shifts.
 * @returns {IAssignPeopleToShiftsByLinesReq} - The request object for assigning people to shifts by lines.
 */
const preparePeopleSchedulingRequest = (
    line_group_id: number,
    lines: ILineData[],
    child_lines: ILineData[],
    shifts: IShiftLineRec[],
    time_period_tag: string,
    scheduling_params: IPeopleSchedulingParams
): IAssignPeopleToShiftsByLinesReq => {
    const shift_tags_set: Set<string> = new Set(timePeriodTagToShiftTags(time_period_tag));
    const line_id_to_line_uuid_map: Map<number, string> = new Map(lines.map(line => [line.id, line.uuid]));
    const line_uuid_to_shifts_map: Map<string, IShiftLineRec[]> = new Map();

    for (const shift of shifts) {
        if (!shift_tags_set.has(shiftTag(shift.year, shift.week, shift.shift_number))) {
            continue;
        }

        let line_shifts = line_uuid_to_shifts_map.get(shift.line_uuid);
        if (line_shifts === undefined) {
            line_shifts = [];
            line_uuid_to_shifts_map.set(shift.line_uuid, line_shifts);
        }

        line_shifts.push(shift);
    }

    return {
        line_group_id,
        lines: child_lines.map(child_line => {
            const parent_line_uuid = line_id_to_line_uuid_map.get(child_line.parent_id ?? 0) ?? "";
            const line_shifts = line_uuid_to_shifts_map.get(parent_line_uuid) ?? [];

            return {
                uuid: child_line.uuid,
                skip: false,
                shifts: line_shifts.map(shift => ({
                    enabled: shift.enabled,
                    year: shift.year,
                    week: shift.week,
                    shift: shift.shift_number,
                    people: [],
                })),
            };
        }),
        scheduling_params,
        shifts: Array.from(shift_tags_set).map(shift_tag => {
            const shift_number = shiftNumber(fromShiftTag(shift_tag));
            const { year, week, shift } = shift_number;
            return { year, week, shift };
        }),
    };
};

/**
 * Sums the factors of competences for child lines based on the enabled shifts and scheduled people.
 * It calculates the total number of needed and available people for each competence.
 * @param {ILineData[]} child_lines - The list of child lines with competence factors.
 * @param {ILineData[]} lines - The list of parent lines.
 * @param {IShiftLineRec[]} shifts - The list of shifts for the given time period.
 * @param {IPeopleScheduleLine[]} people_schedule - Schedule of people for the child lines and shifts.
 * @param {string} time_period_tag - The time period tag to filter shifts.
 * It can be either a date string in format "YYYY-MM-DD" or a shift tag in format "YYYYWWSS".
 * @returns {FactorSumsByCompetence} - A map where the key is the competence ID and the value is an object
 * with the total number of needed and available people for that competence.
 */
const sumLineFactorsByCompetence = (
    child_lines: ILineData[],
    lines: ILineData[],
    shifts: IShiftLineRec[],
    people_schedule: IPeopleScheduleLine[],
    time_period_tag: string
): FactorSumsByCompetence => {
    // Set of shift tags for the given time period tag
    const shift_tags_set: Set<string> = new Set(timePeriodTagToShiftTags(time_period_tag));

    // Lookup map for line IDs by line UUIDs
    const line_uuid_to_line_id_map: Map<string, number> = new Map(lines.map(line => [line.uuid, line.id]));

    // Lookup map for number of enabled shifts by line_id
    const line_id_to_enabled_shift_count_map: Map<number, number> = new Map();
    for (const shift of shifts) {
        if (!shift_tags_set.has(shiftTag(shift.year, shift.week, shift.shift_number))) {
            continue;
        }

        const line_id = line_uuid_to_line_id_map.get(shift.line_uuid);
        if (line_id === undefined) {
            continue;
        }

        // Count the number of enabled shifts for each line_id.
        const count = line_id_to_enabled_shift_count_map.get(line_id) ?? 0;
        line_id_to_enabled_shift_count_map.set(line_id, count + Number(shift.enabled));
    }

    const child_line_uuid_to_scheduled_count_map: Map<string, number> = new Map();
    for (const line of people_schedule) {
        if (line.skip) {
            continue;
        }

        const line_uuid = line.uuid;
        const scheduled_count = line.shifts.reduce((sum, shift) => sum + shift.people.length, 0);
        child_line_uuid_to_scheduled_count_map.set(line_uuid, scheduled_count);
    }

    const result: FactorSumsByCompetence = new Map();
    for (const child_line of child_lines) {
        if (child_line.factor === null) {
            continue;
        }

        const { competence_id, factor } = child_line;
        if (competence_id === null) {
            continue;
        }

        const parent_id = child_line.parent_id;
        if (parent_id === null) {
            continue;
        }

        let factor_sum = result.get(competence_id);
        if (factor_sum === undefined) {
            factor_sum = { needed: 0, available: 0 };
            result.set(competence_id, factor_sum);
        }

        const enabled_shift_count = line_id_to_enabled_shift_count_map.get(parent_id) ?? 0;
        const scheduled_count = child_line_uuid_to_scheduled_count_map.get(child_line.uuid) ?? 0;
        result.set(competence_id, {
            needed: factor_sum.needed + factor * enabled_shift_count,
            available: factor_sum.available + Math.min(factor, 1) * scheduled_count,
        });
    }

    return result;
};

/**
 * Sums the line factors for each competence.
 * @param {FactorSumsByCompetence} factor_sums_by_competence - A map where the key is the competence
 * ID and the value is an object with the total number of needed and available people for that
 * competence.
 * @returns {FactorSum} - An object containing the total number of needed and available people
 * across all competences.
 */
const sumLineFactors = (factor_sums_by_competence: FactorSumsByCompetence): FactorSum => {
    const sum: FactorSum = { needed: 0, available: 0 };
    for (const factor_sum of factor_sums_by_competence.values()) {
        sum.needed += factor_sum.needed;
        sum.available += factor_sum.available;
    }

    return sum;
};

const PreplanningShiftCompetencesModal = (props: {
    available_people: IShiftPersonLineRecPerson[];
    child_lines: ILineData[];
    line_group_uuid: string;
    lines: ILineData[];
    shifts: IShiftLineRec[];
    show: boolean;
    time_period_tag: string;
    onHide: () => void;
}) => {
    const { child_lines, lines, line_group_uuid, shifts, show, time_period_tag, onHide } = props;

    const [error, setError] = useState<string>("");
    const [is_loading, setIsLoading] = useState<boolean>(false);
    const [competences, setCompetences] = useState<Map<number, ICompetenceData>>(new Map());
    const [people_schedule, setPeopleSchedule] = useState<IPeopleScheduleLine[]>([]);
    const [scheduling_params, setSchedulingParams] = useState<IPeopleSchedulingParams>(
        loadPeopleSchedulingParams(FIELD_PEOPLE_SCHEDULING_PARAMS, DEFAULT_PEOPLE_SCHEDULING_PARAMS)
    );

    const handleSchedulingParamsChange = (params: IPeopleSchedulingParams) => {
        setSchedulingParams(params);
        storePeopleSchedulingParams(FIELD_PEOPLE_SCHEDULING_PARAMS, params);
    };

    useEffect(() => {
        const loadSchedule = async () => {
            if (time_period_tag === "") {
                return;
            }

            setError("");
            setIsLoading(true);
            try {
                const lineGroup = getLineGroupForUser(line_group_uuid);
                if (!lineGroup) {
                    throw new Error("Line group not found or inaccessible");
                }

                const { lines: schedule_lines } = await getBackend().manufacturing.assignPeopleToShiftsByLines(
                    preparePeopleSchedulingRequest(
                        lineGroup.id,
                        lines,
                        child_lines,
                        shifts,
                        time_period_tag,
                        scheduling_params
                    )
                );
                const { competences: line_competences } = await getBackend().manufacturing.getCompetences({
                    ids: Array.from(competence_id_to_factor_sums_map.keys()),
                });

                setCompetences(new Map(line_competences.map(comp => [comp.id, comp])));
                setPeopleSchedule(schedule_lines);
            } catch (error) {
                setError(
                    translate(
                        "ShiftAssignmentWeekPool.error_while_scheduling_people",
                        "An error occurred while scheduling people"
                    )
                );
                console.error("Error assigning people to lines:", error);
            }

            setIsLoading(false);
        };

        loadSchedule();
    }, [line_group_uuid, lines, shifts, time_period_tag, scheduling_params]);

    const competence_id_to_factor_sums_map = useMemo(() => {
        return sumLineFactorsByCompetence(child_lines, lines, shifts, people_schedule, time_period_tag);
    }, [child_lines, lines, shifts, people_schedule, time_period_tag]);

    const factor_sums = useMemo(
        () => sumLineFactors(competence_id_to_factor_sums_map),
        [competence_id_to_factor_sums_map]
    );

    return (
        <Modal
            className="preplanning-shift-competences-modal"
            dialogClassName="modal-dialog-scrollable"
            show={show}
            onHide={onHide}
            animation={false}
        >
            <Modal.Header>
                <Modal.Title>
                    {translate("Manufacturing.People.competences", "Competences")} -{" "}
                    {parseTimePeriodTag(time_period_tag)}
                </Modal.Title>
                <button type="button" className="close" onClick={onHide}>
                    <span>×</span>
                </button>
            </Modal.Header>
            <Toolbar
                className="preplanning-shift-competences-modal__toolbar"
                drawerClassName="preplanning-shift-competences-modal__toolbar-drawer"
            >
                <ToolbarGroup>
                    <ToolbarItem>
                        <label className="sr-only" htmlFor="preplanning-scheduling-settings-strategy">
                            <span>
                                {translate(
                                    "ShiftAssignmentPoolEdit.automatic_scheduling_strategy",
                                    "Automatic scheduling strategy"
                                )}
                            </span>
                        </label>
                        <Select
                            inputId="preplanning-scheduling-settings-strategy"
                            className="react-select react-select--sm"
                            classNamePrefix="react-select"
                            menuPosition="fixed"
                            value={getPeopleSchedulingStrategyOption(scheduling_params.strategy)}
                            options={getPeopleSchedulingStrategyOptions()}
                            isDisabled={is_loading}
                            onChange={(option: PeopleSchedulingStrategyOption) => {
                                handleSchedulingParamsChange({
                                    ...scheduling_params,
                                    strategy: option.value,
                                });
                            }}
                        />
                    </ToolbarItem>
                </ToolbarGroup>
                <ToolbarGroup align="end" grow>
                    <ToolbarItem>
                        <PeopleSchedulingSettings
                            scheduling_params={scheduling_params}
                            disabled={is_loading}
                            onSchedulingParamsChange={handleSchedulingParamsChange}
                        />
                    </ToolbarItem>
                </ToolbarGroup>
            </Toolbar>
            <Modal.Body>
                {error && <ErrorComponent className="mt-0" type="error" msg={error} />}
                <div className="table-responsive">
                    <table
                        className={classNames("preplanning-shift-competences-modal__table table table-bordered", {
                            "is-loading": is_loading,
                        })}
                    >
                        <thead>
                            <tr>
                                <th>{translate("Manufacturing.People.competence", "Competence")}</th>
                                <th>{translate("common.needed", "Needed")}</th>
                                <th>{translate("common.available", "Available")}</th>
                                <th>{translate("common.difference", "Difference")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...competence_id_to_factor_sums_map.entries()].map(([competence_id, factor_sum]) => (
                                <tr key={competence_id}>
                                    <th>
                                        <span>{competences.get(competence_id)?.title ?? "-"}</span>
                                    </th>
                                    <td>
                                        <span>{niceNumber(factor_sum.needed)}</span>
                                    </td>
                                    <td>
                                        <span>{niceNumber(factor_sum.available)}</span>
                                    </td>
                                    <td>
                                        <span>{niceNumber(factor_sum.needed - factor_sum.available)}</span>
                                    </td>
                                </tr>
                            ))}
                            <tr>
                                <th>
                                    <strong>{translate("common.sum", "Sum")}</strong>
                                </th>
                                <td>
                                    <strong>{niceNumber(factor_sums.needed)}</strong>
                                </td>
                                <td>
                                    <strong>{niceNumber(factor_sums.available)}</strong>
                                </td>
                                <td>
                                    <strong>{niceNumber(factor_sums.needed - factor_sums.available)}</strong>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default PreplanningShiftCompetencesModal;
